import {useCallback, useEffect, useRef, useState, type ChangeEvent} from 'react';
import userStore from '@/store/admin/userStore';
import {
    calcLastTaxDrawerWidth,
    createLastTaxPreviewUrl,
    isPdfFile,
    lastTaxYoaKey,
    loadLatestLastTaxMeta,
    uploadLastTaxPdf,
    type LastTaxAttachmentMeta,
} from '@/pages/components/tax-computation/lastTaxAttachment';

export type UseLastTaxParams = {
    open: boolean;
    annualId: number;
    companyId: number;
    /** Current YOA (`effectiveYoa`); empty string for single-YOA mode */
    yoa: string;
    onMessage: (message: string) => void;
};

export function useLastTax({
    open,
    annualId,
    companyId,
    yoa,
    onMessage,
}: UseLastTaxParams) {
    const [lastTaxByYoa, setLastTaxByYoa] = useState<Record<string, LastTaxAttachmentMeta | null>>({});
    const [previewUrlByYoa, setPreviewUrlByYoa] = useState<Record<string, string | null>>({});
    const [uploadingLastTax, setUploadingLastTax] = useState(false);
    const [lastTaxDrawerOpen, setLastTaxDrawerOpen] = useState(false);
    const [drawerWidth, setDrawerWidth] = useState(() =>
        typeof window !== 'undefined' ? calcLastTaxDrawerWidth(window.innerWidth) : 794,
    );

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const replaceModeRef = useRef(false);
    const previewUrlByYoaRef = useRef(previewUrlByYoa);
    previewUrlByYoaRef.current = previewUrlByYoa;

    const yoaKey = lastTaxYoaKey(yoa);
    const currentMeta = lastTaxByYoa[yoaKey] ?? null;
    const currentPreviewUrl = previewUrlByYoa[yoaKey] ?? null;
    const hasLastTax = !!currentMeta?.fileKey;

    const revokePreview = useCallback((key: string) => {
        const url = previewUrlByYoaRef.current[key];
        if (url) {
            URL.revokeObjectURL(url);
        }
        setPreviewUrlByYoa((prev) => {
            if (!(key in prev)) return prev;
            const next = {...prev};
            next[key] = null;
            return next;
        });
    }, []);

    const ensurePreviewUrl = useCallback(async (meta: LastTaxAttachmentMeta, key: string) => {
        if (!companyId || !annualId || !meta.fileKey) return null;
        const existing = previewUrlByYoaRef.current[key];
        if (existing) return existing;
        const url = await createLastTaxPreviewUrl({
            companyId,
            annualId,
            fileKey: meta.fileKey,
        });
        setPreviewUrlByYoa((prev) => ({...prev, [key]: url}));
        return url;
    }, [annualId, companyId]);

    const refreshMetaForYoa = useCallback(async (nextYoa: string) => {
        if (!open || !annualId) return;
        const key = lastTaxYoaKey(nextYoa);
        try {
            const meta = await loadLatestLastTaxMeta(annualId, nextYoa);
            setLastTaxByYoa((prev) => ({...prev, [key]: meta}));
            if (!meta) {
                revokePreview(key);
                return null;
            }
            return meta;
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load Last Tax.';
            onMessage(msg);
            return null;
        }
    }, [annualId, onMessage, open, revokePreview]);

    useEffect(() => {
        const onResize = () => setDrawerWidth(calcLastTaxDrawerWidth(window.innerWidth));
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        if (!open) {
            setLastTaxDrawerOpen(false);
            Object.keys(previewUrlByYoaRef.current).forEach((key) => {
                const url = previewUrlByYoaRef.current[key];
                if (url) URL.revokeObjectURL(url);
            });
            setPreviewUrlByYoa({});
            setLastTaxByYoa({});
            return;
        }
        if (!annualId) return;
        let cancelled = false;
        void (async () => {
            const meta = await refreshMetaForYoa(yoa);
            if (cancelled) return;
            if (!meta) {
                setLastTaxDrawerOpen(false);
                return;
            }
            try {
                await ensurePreviewUrl(meta, lastTaxYoaKey(yoa));
            } catch {
                // preview can be built on demand when drawer opens
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, annualId, yoa, refreshMetaForYoa, ensurePreviewUrl]);

    const closeDrawer = useCallback(() => {
        setLastTaxDrawerOpen(false);
    }, []);

    const openDrawer = useCallback(async () => {
        if (!currentMeta) return;
        try {
            await ensurePreviewUrl(currentMeta, yoaKey);
            setLastTaxDrawerOpen(true);
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to open Last Tax preview.';
            onMessage(msg);
        }
    }, [currentMeta, ensurePreviewUrl, onMessage, yoaKey]);

    const handleLastTaxClick = useCallback(() => {
        if (uploadingLastTax) return;
        if (!hasLastTax) {
            replaceModeRef.current = false;
            fileInputRef.current?.click();
            return;
        }
        if (lastTaxDrawerOpen) {
            closeDrawer();
            return;
        }
        void openDrawer();
    }, [closeDrawer, hasLastTax, lastTaxDrawerOpen, openDrawer, uploadingLastTax]);

    const handleReplaceClick = useCallback(() => {
        if (uploadingLastTax) return;
        replaceModeRef.current = true;
        fileInputRef.current?.click();
    }, [uploadingLastTax]);

    const handleFileSelected = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        if (!isPdfFile(file)) {
            onMessage('Only PDF files are allowed.');
            return;
        }

        const userId = userStore.getCurrentUserId();
        const replace = replaceModeRef.current;
        replaceModeRef.current = false;

        try {
            setUploadingLastTax(true);
            const meta = await uploadLastTaxPdf({
                file,
                annualId,
                companyId,
                userId,
                yoa,
                replace: replace || hasLastTax,
            });
            revokePreview(yoaKey);
            setLastTaxByYoa((prev) => ({...prev, [yoaKey]: meta}));
            await ensurePreviewUrl(meta, yoaKey);
            setLastTaxDrawerOpen(true);
            onMessage(replace ? 'Last Tax replaced.' : 'Last Tax uploaded.');
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Last Tax upload failed.';
            onMessage(msg);
        } finally {
            setUploadingLastTax(false);
        }
    }, [
        annualId,
        companyId,
        ensurePreviewUrl,
        hasLastTax,
        onMessage,
        revokePreview,
        yoa,
        yoaKey,
    ]);

    return {
        fileInputRef,
        uploadingLastTax,
        lastTaxDrawerOpen,
        drawerWidth,
        hasLastTax,
        currentMeta,
        currentPreviewUrl,
        handleLastTaxClick,
        handleReplaceClick,
        handleFileSelected,
        closeDrawer,
    };
}
